import React, { useState } from "react";
import Seo from "../components/Seo";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, MapPin, Briefcase, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, POSITIONS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/35 focus:outline-none focus:border-steel-light/60 focus:bg-white/[0.05] transition-all";

export default function Careers() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", location: "", experience: "", cover_letter: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const apply = (title) => {
    setForm((f) => ({ ...f, position: title }));
    document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.position) { toast.error(t("Please fill in your name, email and desired position.")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/careers`, form);
      setDone(true);
      toast.success(t("Application submitted! Our team will review it shortly."));
    } catch { toast.error(t("Something went wrong. Please try again.")); }
    setLoading(false);
  };

  return (
    <div data-testid="careers-page" className="bg-ink-950">
      <Seo title="Careers" path="/careers" description="Join North Peak Facility Services in Montreal. Explore cleaning and facility services careers across the Greater Montreal Area." keywords="Cleaning Jobs Montreal, Cleaning Careers Montreal" />
      <PageHero overline={t("Careers")} title={t("Build your career at North Peak.")} subtitle={t("Join a team that treats facility services as a craft — with training, growth, and genuine respect for the people who deliver it.")} image={IMAGES.team} />

      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal><p className="overline text-steel-light mb-5">{t("Open Positions")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-4xl mb-12" style={{ fontWeight: 500 }}>{t("Now hiring across our regions.")}</h2></Reveal>
          <div className="space-y-4">
            {POSITIONS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div data-testid={`position-${i}`} className="glass card-hover rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4 p-7 md:px-9">
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-white" style={{ fontWeight: 500 }}>{t(p.title)}</h3>
                    <div className="flex gap-6 mt-2 text-sm text-white/50">
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {t(p.location)}</span>
                      <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {t(p.type)}</span>
                    </div>
                  </div>
                  <button data-testid={`apply-btn-${i}`} onClick={() => apply(p.title)}
                    className="btn-pill bg-white/5 border border-white/10 text-white px-5 py-2.5 font-600 text-sm flex items-center gap-2 hover:bg-white hover:text-ink-950 transition-colors self-start md:self-auto group">
                    {t("Apply Now")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="apply-form" className="bg-ink-900 py-24 md:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="career-success" className="text-center py-16">
              <div className="h-16 w-16 rounded-3xl bg-steel/20 text-steel-light flex items-center justify-center mx-auto mb-6"><Check className="h-8 w-8" /></div>
              <h2 className="font-serif text-3xl text-white mb-3" style={{ fontWeight: 500 }}>{t("Application received!")}</h2>
              <p className="text-white/55">{t("Thanks for your interest in North Peak. Our talent team will be in touch if there's a match.")}</p>
            </motion.div>
          ) : (
            <>
              <p className="overline text-steel-light mb-4">{t("Apply")}</p>
              <h2 className="font-serif text-gradient text-3xl md:text-4xl mb-10" style={{ fontWeight: 500 }}>{t("Submit your application.")}</h2>
              <form onSubmit={submit} className="space-y-5" data-testid="career-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="career-name" className={inputCls} placeholder={t("Full name *")} value={form.name} onChange={set("name")} />
                  <input data-testid="career-email" type="email" className={inputCls} placeholder={t("Email *")} value={form.email} onChange={set("email")} />
                  <input data-testid="career-phone" className={inputCls} placeholder={t("Phone")} value={form.phone} onChange={set("phone")} />
                  <input data-testid="career-position" className={inputCls} placeholder={t("Position *")} value={form.position} onChange={set("position")} />
                  <input data-testid="career-location" className={inputCls} placeholder={t("Preferred location")} value={form.location} onChange={set("location")} />
                  <input data-testid="career-experience" className={inputCls} placeholder={t("Years of experience")} value={form.experience} onChange={set("experience")} />
                </div>
                <textarea data-testid="career-cover" rows={4} className={inputCls + " resize-none"} placeholder={t("Tell us about yourself")} value={form.cover_letter} onChange={set("cover_letter")} />
                <button type="submit" disabled={loading} data-testid="career-submit"
                  className="btn-pill group w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20 disabled:opacity-50">
                  {loading ? t("Submitting…") : <>{t("Submit Application")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
